import React, { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import { MaterialSymbol } from '@/components/MaterialSymbol';
import { Status } from '@/declarations/models/Status';
import { includeInPageIncludingFolder, includePage } from '@/utils/functions';
import { FolderView } from '@/views/ContentView/PageTree/FolderView';
import { PageFolder } from '@/declarations/models/Folder';
import { notNil } from '@/utils/object';
import { useSearchParams } from 'react-router-dom';
import { findBreadCrumb, findFolder } from '@/utils/folders';
import { Api } from '@/services/Api';
import { Button, Dialog, Typography, useTheme } from '@mui/material';
import { PageFolderAutocomplete } from '@/views/ContentView/PageTree/PageFolderAutocomplete';
import { Page } from '@/declarations/models/Page';
import { useStore } from '@/components/store/Store';
import PageTreeContent from './PageTreeContent';
import ImportedSelectionHeader from './ImportedSelectionHeader';
import { ancestorIds, forgetLastPages, getLastPages } from '../lastPage';
import { scrollIntoViewWhenSettled } from './scrollIntoViewWhenSettled';
import { ContentViewContentContext, useContentView } from '../ContentViewContext';
import Container from '../../../components/Container';
import Styles from '../../../assets/js/Styles';

const CollapseIcon = () => <MaterialSymbol name='expand_more' />;
const ExpandIcon = () => <MaterialSymbol name='chevron_right' />;
const OPENED_PARENT_CLASS = 'page-tree-opened-parent';

const PageTree: FC = () => {
  const { t } = useTranslation('aria');
  const { t: tComponents } = useTranslation('components');
  const { state } = useStore();
  const theme = useTheme();

  const {
    selectionState,
    filterValues,
    hasFilterApplied,
    sortFn,
    pages,
    contentContext,
    siteFolders,
    activePageFolder,
    setActivePageFolder,
    reloadSiteFolders,
    reloadPages,
    showImported,
    selectionMode,
    markedPageIds,
  } = useContentView();

  const { getRootNodes, getChildren, hasChildren, itemCount } = selectionState;
  const isEventView = contentContext === ContentViewContentContext.EVENT;

  // Imported pages keep the parent links of their source structure. A page whose parent is not itself in the
  // imported list is a root of that structure; when any page sits under another one, the view is a tree.
  const importedRoots = useMemo<Array<Page>>(() => {
    if (!showImported) {
      return [];
    }
    const importedIds = new Set(pages.map((page) => page.id));
    return pages.filter((page) => !page.parent_id || !importedIds.has(page.parent_id));
  }, [showImported, pages]);
  const showImportedTree = showImported && importedRoots.length < pages.length;
  // The imported view counts as a filter, but only an actual search or category filter should force the tree open;
  // otherwise the imported tree could never be collapsed.
  const expandForFilter =
    hasFilterApplied &&
    (!showImportedTree ||
      !!filterValues.query ||
      !!filterValues.main_category ||
      !!filterValues.sub_categories.length ||
      !!filterValues.statuses.length);

  const visibleNodeIds = useMemo<null | Array<string | number>>(() => {
    if (!hasFilterApplied) {
      return null;
    }

    const getVisibleNodeIds = (page: Page): Array<string | number> => {
      const key = page.id || 0;

      if (hasChildren(key)) {
        const visibleChildren = getChildren(key).reduce<Array<string | number>>((ids, child) => {
          ids.push(...getVisibleNodeIds(child).filter((id) => !ids.includes(id)));
          return ids;
        }, []);

        if (visibleChildren.length > 0) {
          visibleChildren.push(key);
          return visibleChildren;
        }
      }

      return includePage(filterValues, page) ? [key] : [];
    };

    return (showImported ? importedRoots : getRootNodes()).reduce<Array<number | string>>((ids, root) => {
      ids.push(...getVisibleNodeIds(root));
      return ids;
    }, []);
  }, [filterValues, hasFilterApplied, getRootNodes, getChildren, hasChildren, showImported, importedRoots]);

  const [userExpandedIds, setUserExpandedIds] = React.useState<string[]>([]);
  const [searchExpandedIds, setSearchExpandedIds] = React.useState<string[]>([]);

  // The level last opened is shaded for as long as it stays open, until another level is opened
  const [openedParentIds, setOpenedParentIds] = React.useState<Array<string>>([]);

  useEffect(() => {
    setSearchExpandedIds((prev) => {
      if (expandForFilter && !!visibleNodeIds?.length) {
        const newIds = visibleNodeIds.map(String);
        return prev.length === newIds.length && prev.every((id) => newIds.includes(id)) ? prev : newIds;
      }

      return prev.length ? [] : prev;
    });
  }, [expandForFilter, visibleNodeIds]);

  // Open the way down to the pages last created, edited or moved and scroll to the first. Once per pages and place, so
  // they can still be closed; moving the same pages again shows them anew
  const selectedSiteId = state.selectedSite?.id;
  const revealedPagesKey = useRef<string | null>(null);
  const cancelScroll = useRef<() => void>(undefined);
  useEffect(() => () => cancelScroll.current?.(), []);
  useEffect(() => {
    const lastPages = (selectedSiteId ? getLastPages(selectedSiteId) : [])
      .map((id) => pages.find((page) => page.id === id))
      .filter(notNil);
    const key = `${selectedSiteId}:${lastPages.map((page) => `${page.id}/${page.parent_id}`)}`;
    if (!lastPages.length || revealedPagesKey.current === key) return;
    revealedPagesKey.current = key;
    const ancestors = lastPages.map((page) => ancestorIds(pages, page.id!));
    setOpenedParentIds([...new Set(ancestors.map((ids) => ids[0]).filter(notNil))]);
    const ids = ancestors.flat();
    if (ids.length) setUserExpandedIds((prev) => [...new Set([...prev, ...ids])]);
    cancelScroll.current?.();
    // The row, not the item with all its children
    cancelScroll.current = scrollIntoViewWhenSettled(`[data-page-tree-id="${lastPages[0].id}"] > .MuiTreeItem-content`);
  }, [selectedSiteId, pages]);

  const expandedNodes = useMemo(() => {
    return expandForFilter ? [...new Set([...userExpandedIds, ...searchExpandedIds])] : userExpandedIds;
  }, [expandForFilter, userExpandedIds, searchExpandedIds]);

  const handleNodeToggle = (_: React.SyntheticEvent | null, newExpanded: string[]) => {
    // Opened or closed by hand: the pages last created or edited are not opened again when coming back
    if (state.selectedSite?.id) forgetLastPages(state.selectedSite.id);
    const opened = newExpanded.filter((id) => !expandedNodes.includes(id));
    setOpenedParentIds((prev) => (opened.length ? opened : prev.filter((id) => newExpanded.includes(id))));
    setUserExpandedIds((prev) =>
      prev.length === newExpanded.length && prev.every((id) => newExpanded.includes(id)) ? prev : newExpanded,
    );
  };

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.has('folder')) {
      const folderId = parseInt(searchParams.get('folder') as string, 10);
      const folder = findFolder(siteFolders, folderId);

      if (folder) {
        setActivePageFolder(folder);
      } else {
        setActivePageFolder(null);
      }
    } else {
      setActivePageFolder(null);
    }
  }, [siteFolders, searchParams, setActivePageFolder]);

  const [folderBreadcrumbs, setFolderBreadcrumbs] = React.useState<PageFolder[]>([]);

  useEffect(() => {
    if (activePageFolder) {
      const breadCrumbs = findBreadCrumb(siteFolders, activePageFolder);
      setFolderBreadcrumbs(breadCrumbs);
    } else {
      setFolderBreadcrumbs([]);
    }
  }, [activePageFolder, siteFolders]);

  const createNewFolder = async (title: string) => {
    if (!state.selectedSite?.id) {
      console.error('Cannot create folder: no selected site');
      return;
    }

    await Api.createFolder(state.selectedSite.id, { title, parent_id: activePageFolder?.id }).fetchDirect(null);
    reloadSiteFolders();
  };

  const deleteFolder = async (folderId: number) => {
    if (!state.selectedSite?.id) {
      console.error('Cannot create folder: no selected site');
      return;
    }

    await Api.deleteFolder(state.selectedSite.id, folderId).fetchDirect(null);
    reloadSiteFolders();
    reloadPages();
  };

  const [foldersToMove, setFoldersToMove] = React.useState<PageFolder[]>([]);
  const [showMoveFolderDialog, setShowMoveFolderDialog] = React.useState(false);
  const [moveTargetFolder, setMoveTargetFolder] = React.useState<PageFolder | null>(null);

  const confirmMoveFolders = async () => {
    const siteId = state.selectedSite?.id;

    if (!siteId) {
      console.error('Cannot move folders: no selected site');
      return;
    }

    const requests = foldersToMove.map((folder) =>
      Api.updateFolder(siteId, folder.id, {
        ...folder,
        parent_id: moveTargetFolder?.id,
      }).fetchDirect(null),
    );

    await Promise.all(requests);
    reloadSiteFolders();
    reloadPages();
    setShowMoveFolderDialog(false);
  };

  const initMoveFolders = (folders: PageFolder[]) => {
    setFoldersToMove(folders);
    setShowMoveFolderDialog(true);
  };

  const updateFolder = async (folder: PageFolder) => {
    const siteId = state.selectedSite?.id;

    if (!siteId) {
      console.error('Cannot move folders: no selected site');
      return;
    }

    await Api.updateFolder(siteId, folder.id, folder).fetchDirect(null);
    reloadSiteFolders();
  };

  const navigateToFolder = (folder: PageFolder | null) => {
    if (!folder) {
      searchParams.delete('folder');
      setSearchParams(searchParams);
      return;
    }

    setSearchParams({ folder: String(folder.id) });
  };

  const renderNode = useCallback(
    (page: Page) => {
      const pageId = page.id || 0;
      const childNodes = getChildren(pageId).sort(sortFn);

      if (visibleNodeIds && !visibleNodeIds.includes(pageId)) {
        return null;
      }

      return (
        <TreeItem
          key={pageId}
          itemId={String(pageId)}
          data-page-tree-id={pageId}
          slotProps={
            openedParentIds.includes(String(pageId)) && expandedNodes.includes(String(pageId))
              ? { content: { className: OPENED_PARENT_CLASS } }
              : undefined
          }
          label={
            <PageTreeContent
              page={page}
              diffuse={hasFilterApplied && !includePage(filterValues, page)}
              marked={markedPageIds.includes(pageId)}
            />
          }>
          {hasChildren(pageId) ? childNodes.map(renderNode) : null}
        </TreeItem>
      );
    },
    [
      getChildren,
      hasChildren,
      hasFilterApplied,
      visibleNodeIds,
      filterValues,
      sortFn,
      markedPageIds,
      openedParentIds,
      expandedNodes,
    ],
  );

  if (!itemCount) {
    return showImported ? (
      <Container fullWidth pl={2}>
        {tComponents('PageTree.NoImportedPages')}
      </Container>
    ) : null;
  }

  const frontpage = pages?.find((page) => page.id === state.selectedSiteDefaultPageId);

  const pagesToDisplay = pages?.map((page) => {
    if (includeInPageIncludingFolder(filterValues, page, activePageFolder)) {
      return (
        <Container
          fullWidth
          key={page.id}
          pl={isEventView ? 0 : 2}
          sx={{
            borderRadius: Styles.Dimensions.RADIUS_ROUNDNESS_DEFAULT,
            backgroundColor:
              isEventView && page.status === Status.PUBLISHED
                ? Styles.Colors.STRONG_GREEN_TRANSPARENT
                : Styles.Colors.LIGHT_GREY,
            '&:hover': {
              backgroundColor:
                isEventView && page.status === Status.PUBLISHED
                  ? Styles.Colors.MEDIUM_GREEN_TRANSPARENT
                  : Styles.Colors.LIGHTEST_GREY,
            },
          }}>
          <PageTreeContent page={page} displayBreadcrumbs={!isEventView} />
        </Container>
      );
    }

    return null;
  });

  // One tree for both views: the ordinary one is rooted at the site's pages, the imported one at the pages whose
  // parent is not itself imported. Everything else about it - component, icons, rows, colours - is the same.
  const treeRoots = showImportedTree
    ? [...importedRoots]
    : getRootNodes(state.selectedSiteDefaultPageId ?? undefined).filter(
        (page) => page.id !== state.selectedSiteDefaultPageId,
      );
  const pageTree = (
    <SimpleTreeView
      sx={{
        width: '100%',
        // No hover or focus background, which runs out to the left edge; the row's bottom line is darkened on hover
        // instead, and keyboard focus gets an outline
        '& .MuiTreeItem-content:hover, & .MuiTreeItem-content.Mui-focused': { backgroundColor: 'transparent' },
        '& .MuiTreeItem-root:focus-visible > .MuiTreeItem-content': {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: '-2px',
        },
        // The opened level: grey from its indentation, not from the left edge, whether hovered or not
        [`& .MuiTreeItem-content.${OPENED_PARENT_CLASS}`]: {
          backgroundColor: 'transparent',
          backgroundImage: `linear-gradient(to right, transparent calc(var(--TreeView-itemChildrenIndentation) * var(--TreeView-itemDepth)), ${theme.palette.action.hover} 0)`,
        },
      }}
      id='page-tree'
      aria-label={t(`components.PageTree.${showImportedTree ? 'ImportedPageTreeDescription' : 'PageTreeDescription'}`, {
        siteName: state.selectedSite?.name || 'unknown',
      })}
      // More than the default 12px, so the levels are easier to tell apart
      itemChildrenIndentation={18}
      expandedItems={expandedNodes}
      onExpandedItemsChange={handleNodeToggle}
      slots={{
        collapseIcon: CollapseIcon,
        expandIcon: ExpandIcon,
      }}
      disableSelection>
      {treeRoots.sort(sortFn).map(renderNode)}
    </SimpleTreeView>
  );

  return (
    <>
      {showImported && selectionMode && <ImportedSelectionHeader inTree={showImportedTree} />}
      {showImportedTree && (
        // The imported view has a grey background; the rows stay white, as in the ordinary view, on a white sheet.
        <Container
          fullWidth
          column
          p={1}
          sx={{ backgroundColor: Styles.Colors.WHITE, borderRadius: Styles.Dimensions.RADIUS_ROUNDNESS_DEFAULT }}>
          {pageTree}
        </Container>
      )}
      {!showImportedTree && (hasFilterApplied || isEventView) && (
        <>
          {isEventView && state.selectedSite?.id != null && (
            <FolderView
              sx={{
                mb: 1,
              }}
              siteFolders={siteFolders}
              activePageFolder={activePageFolder}
              breadcrumbs={folderBreadcrumbs}
              onFolderClick={navigateToFolder}
              onCreateNewClick={createNewFolder}
              onFolderDelete={deleteFolder}
              onFolderEdit={updateFolder}
              onFolderMove={initMoveFolders}
            />
          )}

          {pagesToDisplay}

          {Boolean(pagesToDisplay?.filter(notNil).length === 0) && (
            <Container fullWidth pl={2}>
              {tComponents(showImported ? 'PageTree.NoImportedPages' : 'PageTree.NoPagesFound')}
            </Container>
          )}
        </>
      )}
      {!showImportedTree && !hasFilterApplied && !isEventView && (
        <>
          {frontpage && (
            <Container
              fullWidth
              px={1}
              sx={{
                backgroundColor: Styles.Colors.LIGHTEST_BLUE,
                '&:hover': {
                  backgroundColor: Styles.Colors.LIGHT_BLUE,
                },
                pl: 3,
              }}>
              <PageTreeContent page={frontpage} hideBorder displayBreadcrumbs={false} />
            </Container>
          )}

          {pageTree}
        </>
      )}

      <Dialog
        open={showMoveFolderDialog}
        onClose={() => {
          setShowMoveFolderDialog(false);
          setMoveTargetFolder(null);
        }}>
        <Container
          fullWidth
          column
          sx={{
            p: 2,
            gap: 2,
            minWidth: '400px',
          }}>
          <Typography variant='h4'>{tComponents('FolderView.MoveDialogTitle')}</Typography>
          <PageFolderAutocomplete
            folders={siteFolders.filter((folder) => foldersToMove.every((f) => f.id !== folder.id))}
            onChange={(targetFolder) => {
              if (targetFolder?.id) {
                setMoveTargetFolder(targetFolder);
              } else {
                setMoveTargetFolder(null);
              }
            }}
          />
          <Container>
            <Button onClick={() => setShowMoveFolderDialog(false)}>{tComponents('FolderView.cancel')}</Button>
            <Button color='primary' variant='contained' onClick={() => confirmMoveFolders()}>
              {tComponents('FolderView.confirmMoveButton')}
            </Button>
          </Container>
        </Container>
      </Dialog>
    </>
  );
};

export default PageTree;
